import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, finalize, tap } from 'rxjs';
import { LoadStates } from '@core/locations/store/actions/states.actions';
import { LocationState } from '@core/locations/models/location-state.interface';
import { StatesService } from '@core/locations/services/states.service';

export interface StatesStateModel {
  loadersByCountryId: Record<number, boolean>;
  statesByCountryId: Record<number, LocationState[]>;
}

@State<StatesStateModel>({
  name: 'locationStates',
  defaults: {
    loadersByCountryId: {},
    statesByCountryId: {},
  },
})
@Injectable()
export class StatesState {
  private readonly statesService = inject(StatesService);

  @Selector()
  static statesByCountryId(state: StatesStateModel): Record<number, LocationState[] | undefined> {
    return state.statesByCountryId;
  }

  @Selector()
  static loadersByCountryId(state: StatesStateModel): Record<number, boolean> {
    return state.loadersByCountryId;
  }

  @Selector()
  static areStatesLoading(state: StatesStateModel): boolean {
    return Object.values(state.loadersByCountryId).some(loader => loader);
  }

  @Action(LoadStates)
  loadStates(context: StateContext<StatesStateModel>, { countryId }: LoadStates): Observable<LocationState[]> {
    const { statesByCountryId, loadersByCountryId } = context.getState();
    if (statesByCountryId[countryId]) {
      return EMPTY;
    }

    context.patchState({
      loadersByCountryId: {
        ...loadersByCountryId,
        [countryId]: true,
      },
    });
    return this.statesService.getStates(countryId).pipe(
      tap(states => {
        context.patchState({
          statesByCountryId: {
            ...statesByCountryId,
            [countryId]: states,
          },
        });
      }),
      finalize(() => {
        context.patchState({
          loadersByCountryId: {
            ...loadersByCountryId,
            [countryId]: false,
          },
        });
      })
    );
  }
}
